import * as React from 'react';
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    AnnualPolicyInfo,
    AnnualPolicyPremiumTypeEnum,
    FalconPaymentResponse,
    ICacheManager,
    MissedAnnualPaymentInfo,
    MissedPayment,
    Types,
    VerifyNewCardResponse
} from "@ferry/journey-services";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import FalconHeader from "../../../../components/navigation/FalconHeader";
import {Navigate} from "react-router-dom";
import {getAmountWithDecimalDigits, getSessionStorage} from "../../../../CommonFunctions";
import {Types as CustomerPortalTypes} from "../../../../Types";
import type {ICustomerPortalService} from "../../../../services/ICustomerPortalService";
import {getBrandFromPolicyNumber, resourcesHelper, Footer} from "@ferry/portal-core";

interface IParams {
    policyNumber: string
    paymentReference: string
    invoiceId: string
    [key: string]: string | undefined
}

interface IState {
    paymentSuccess: boolean;
    paymentCancelled: boolean;
    cardVerified: boolean;
    policyNumber: string;
    paymentAmountReceived: boolean;
    premiumType?: string;
    dataLoaded: boolean;
    isChargeBack: boolean;
    reuseCard: boolean;
    failureRedirectUrl: string;
    cardNumberObfuscated: string;
    invoiceId: string;
}

@authorize
export class PaymentProcessingPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private readonly pluginTemplates = new PluginTemplates();

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private readonly cacheManager: ICacheManager;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            paymentSuccess: false,
            paymentCancelled: false,
            cardVerified: false,
            policyNumber: getSessionStorage("missedPaymentPolicyNumber"),
            paymentAmountReceived: false,
            dataLoaded: false,
            isChargeBack: getSessionStorage("isChargeBack"),
            reuseCard: getSessionStorage("reuseCard"),
            failureRedirectUrl: "",
            cardNumberObfuscated: "",
            invoiceId: this.props.params.invoiceId
        };

        this.verifyNewCardMissedPayment = this.verifyNewCardMissedPayment.bind(this);
        this.verificationNeeded = this.verificationNeeded.bind(this);
        this.existingCardMissedPayment = this.existingCardMissedPayment.bind(this);
        this.getRedirectUrl = this.getRedirectUrl.bind(this);
        this.getReferrerJourney = this.getReferrerJourney.bind(this);
        this.obtainPaymentAmount = this.obtainPaymentAmount.bind(this);
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Payment processing");

        this.customerPortalService.getAnnualPolicyInfo(this.props.params.policyNumber)
            .subscribe((policy: AnnualPolicyInfo) => {
                let invoiceId: string = this.props.params.invoiceId;

                // A bit of a workaround, ideally invoiceId should be populated in the props already. Raised FER-7186 to address
                if (!this.state.invoiceId) {
                    const unpaidInstallmentIdToAmount: any = policy.unpaidInstallmentIdToAmount;
                    const missedAnnualPaymentInfo: MissedAnnualPaymentInfo | undefined = policy.missedAnnualPaymentInfo;
                    const unpaidChargebackInvoiceInfo: any = policy.unpaidChargebackInvoiceInfo;

                    if (unpaidInstallmentIdToAmount && Object.keys(unpaidInstallmentIdToAmount).length > 0) {
                        invoiceId = Array.from(Object.keys(unpaidInstallmentIdToAmount))[0];

                        this.setState({
                            invoiceId
                        })
                    } else if (missedAnnualPaymentInfo?.invoiceId) {
                        invoiceId = missedAnnualPaymentInfo.invoiceId.toString()
                        this.setState({
                            invoiceId
                        })
                    } else if (unpaidChargebackInvoiceInfo && Object.keys(unpaidChargebackInvoiceInfo).length > 0) {
                        invoiceId = Array.from(Object.keys(unpaidChargebackInvoiceInfo))[0];

                        this.setState({
                            invoiceId
                        })
                    }
                }

                if (this.verificationNeeded()) {
                    this.verifyNewCardMissedPayment(this.props.params.paymentReference, invoiceId);
                } else {
                    this.existingCardMissedPayment(invoiceId);
                }

                this.setState({
                    premiumType: policy.premiumType,
                    dataLoaded: true
                })
            })
    }

    public render() {
        const dataLoaded = this.state.dataLoaded;
        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                <FalconHeader/>
                {dataLoaded && this.renderPageContents()}
                <Footer/>
            </React.Fragment>
        );
    }

    private renderPageContents() {
        const {
            paymentSuccess,
            paymentCancelled,
            cardVerified,
            paymentAmountReceived
        } = this.state;

        const brandName: string = this.props.params.policyNumber ?
            getBrandFromPolicyNumber(this.props.params.policyNumber)! :
            resourcesHelper.THEME_NAME;

        if (cardVerified) {
            if (!paymentSuccess && !paymentCancelled) {
                return <Navigate to={{
                    pathname: this.state.failureRedirectUrl
                }} state={{verifyCardFailed: true}}
                />
            } else if (paymentSuccess && paymentAmountReceived) {
                return <Navigate to={this.getRedirectUrl()}/>
            } else if (!paymentSuccess) {
                return <Navigate to={this.getRedirectUrl()}/>
            }
        }

        return (
            <div className="panel panel-processing">
                <h1 className="panel-heading">Processing...</h1>
                <h2 className="text-center h5">Once your {brandName} insurance is set up,
                    you will see a payment confirmation screen and you will receive a confirmation email.</h2>
                <p>Please wait...</p>
                <p>Don't refresh or close your browser...</p>
                <img className="car" src="/img/Flourishes-5.svg" alt=""/>
            </div>
        )
    }

    private obtainPaymentAmount(cardNumberObfuscated: string): void {
        const referrerJourney: string = this.getReferrerJourney();
        window.sessionStorage.setItem("showPopup", "true");
        window.sessionStorage.setItem("referrerJourney", referrerJourney);

        if (cardNumberObfuscated) {
            window.sessionStorage.setItem("cardNumberObfuscated", cardNumberObfuscated);
        } else {
            window.sessionStorage.removeItem("cardNumberObfuscated");
        }

        this.cacheManager.clearCache("getPolicies");

        if (this.verificationNeeded()) {
            this.customerPortalService.getSettledAmountByPaymentReference(this.state.policyNumber, this.props.params.paymentReference)
                .subscribe((amount: number) => {
                    window.sessionStorage.setItem("paymentAmount", getAmountWithDecimalDigits(amount).toString());
                    this.setState({paymentAmountReceived: true});
                });
        } else {
            this.setState({paymentAmountReceived: true});
        }
    }

    private getRedirectUrl(): string {
        return this.state.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            ? `/dashboard/monthlyPaymentHistory/${this.state.policyNumber}`
            : `/dashboard/annualPaymentHistory/${this.state.policyNumber}`;
    }

    private getReferrerJourney(): string {
        return this.state.isChargeBack ? "chargeBackPayment" : "wolfMissedPayment";
    }

    private verifyNewCardMissedPayment(paymentReference: string, invoiceId: string): void {
        const policyNumber: string = this.state.policyNumber ?? this.props.params.policyNumber;
        const isChargeback: boolean = this.state.isChargeBack;
        const request: MissedPayment = {
            paymentReferenceId: paymentReference,
            product: "AnnualMotorInsurance",
            invoiceId: Number(invoiceId),
            isChargeback,
            reuseCard: this.state.reuseCard
        };

        this.customerPortalService.completeMissedPaymentNewCard(policyNumber, request)
            .subscribe((response: VerifyNewCardResponse) => {
                const isSuccess = response?.successful;
                const obfuscatedCard = response.paymentCard?.cardNumberObfuscated ?? "";

                if (isSuccess) {
                    this.obtainPaymentAmount(obfuscatedCard);
                }

                this.setState({
                    paymentSuccess: isSuccess,
                    paymentCancelled: response?.cancelled,
                    cardVerified: true,
                    failureRedirectUrl: !isChargeback
                        ? `/dashboard/account/missedPolicyPayment/${policyNumber}`
                        : `/dashboard/account/outstandingPayment/${policyNumber}/${invoiceId}`,
                    cardNumberObfuscated: obfuscatedCard,
                });
            });
    }

    private existingCardMissedPayment(invoiceId: string): void {
        const policyNumber: string = this.state.policyNumber ?? this.props.params.policyNumber;
        if (policyNumber) {
            const failureRedirectUrl: string = this.state.isChargeBack
                ? `/dashboard/account/outstandingPayment/${policyNumber}/${invoiceId}`
                : `/dashboard/account/payOverdueInstalment/${policyNumber}`;

            this.customerPortalService.completeMissedPaymentWithMainPaymentCard(
                policyNumber,
                invoiceId,
                this.state.isChargeBack
            )
                .subscribe((response: FalconPaymentResponse) => {
                    const isSuccess = response?.success;

                    if (isSuccess) {
                        this.obtainPaymentAmount(this.state.cardNumberObfuscated);
                    }

                    this.setState({
                        paymentSuccess: isSuccess,
                        paymentCancelled: false,
                        cardVerified: true,
                        failureRedirectUrl
                    });
                });
        }
    }

    private verificationNeeded() {
        return this.props.params.paymentReference && this.props.params.paymentReference !== 'null';
    }
}

export const PaymentProcessingPage = withRouter<{}, IParams>(PaymentProcessingPagePlain)
